import React, { useState, useEffect } from 'react';

export interface UserPreferences {
  country: string;
  city: string;
  seniority: string;
  industry: string;
  role: string;
}

interface FilterComponentProps {
  initialPreferences: UserPreferences;
  onUpdate: (preferences: UserPreferences) => Promise<void>;
}

interface DropdownProps {
  label: string;
  value: string;
  options: string[];
  fieldKey: keyof UserPreferences;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({ 
  label, 
  value, 
  options, 
  isOpen,
  onToggle,
  onSelect
}) => (
  <div className="flex-1 min-w-0 md:min-w-[200px]">
    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 md:px-4 py-2 md:py-2.5 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-left"
      >
        <span className="text-xs md:text-sm text-gray-900 truncate">{value || `Select ${label}`}</span>
        <svg 
          className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={onToggle}
          />
          <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => onSelect(option)}
                className={`w-full text-left px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm hover:bg-gray-50 transition-colors ${
                  value === option ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  </div>
);

interface TextInputProps {
  label: string;
  value: string;
  fieldKey: keyof UserPreferences;
  placeholder?: string;
  onChange: (value: string) => void;
}

const TextInput: React.FC<TextInputProps> = ({ 
  label, 
  value, 
  placeholder,
  onChange
}) => (
  <div className="flex-1 min-w-0 md:min-w-[200px]">
    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || `Enter ${label.toLowerCase()}`}
      className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-xs md:text-sm text-gray-900"
    />
  </div>
);

export const FilterComponent: React.FC<FilterComponentProps> = ({ 
  initialPreferences, 
  onUpdate 
}) => {
  const [preferences, setPreferences] = useState<UserPreferences>(initialPreferences);
  const [isOpen, setIsOpen] = useState({
    country: false,
    seniority: false,
    industry: false,
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const changed = 
      preferences.country !== initialPreferences.country ||
      preferences.city !== initialPreferences.city ||
      preferences.seniority !== initialPreferences.seniority ||
      preferences.industry !== initialPreferences.industry ||
      preferences.role !== initialPreferences.role;
    setHasChanges(changed);
  }, [preferences, initialPreferences]);

  // Sync with external updates
  useEffect(() => {
    setPreferences(initialPreferences);
  }, [initialPreferences]);

  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
    'France', 'Netherlands', 'Singapore', 'India', 'Ireland'
  ];

  // Seniority options with display labels and Theirstack API values
  const seniorityOptions = [
    { label: 'Junior', value: 'junior' },
    { label: 'Mid-Level', value: 'mid_level' },
    { label: 'Senior', value: 'senior' },
    { label: 'Manager', value: 'staff' },
    { label: 'C-Suite', value: 'c_level' },
  ];
  
  // Helper to get display label from database value
  const getSeniorityLabel = (value: string): string => {
    const option = seniorityOptions.find(opt => opt.value === value);
    return option?.label || value;
  };
  
  // Helper to get database value from display label
  const getSeniorityValue = (label: string): string => {
    const option = seniorityOptions.find(opt => opt.label === label);
    return option?.value || label;
  };

  const industries = [
    'Technology', 'Finance', 'Healthcare', 'Consulting', 'Retail', 
    'Manufacturing', 'Education', 'Media', 'Legal', 'Real Estate'
  ];

  const handleFieldChange = (key: keyof UserPreferences, value: string) => {
    setPreferences({
      ...preferences,
      [key]: value,
    });
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(preferences);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update preferences:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-4 md:mb-6">
      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Your Job Search Preferences</h3>
      
      {/* Location Row: Country + City */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
        <Dropdown 
          label="Country" 
          value={preferences.country} 
          options={countries} 
          fieldKey="country"
          isOpen={isOpen.country}
          onToggle={() => setIsOpen({ ...isOpen, country: !isOpen.country })}
          onSelect={(value) => {
            handleFieldChange('country', value);
            setIsOpen({ ...isOpen, country: false });
          }}
        />
        <TextInput 
          label="City" 
          value={preferences.city} 
          fieldKey="city"
          placeholder="e.g., London, New York"
          onChange={(value) => handleFieldChange('city', value)}
        />
      </div>

      {/* Career Row: Seniority + Industry */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
        <Dropdown 
          label="Seniority Level" 
          value={getSeniorityLabel(preferences.seniority)} 
          options={seniorityOptions.map(opt => opt.label)} 
          fieldKey="seniority"
          isOpen={isOpen.seniority}
          onToggle={() => setIsOpen({ ...isOpen, seniority: !isOpen.seniority })}
          onSelect={(label) => {
            handleFieldChange('seniority', getSeniorityValue(label));
            setIsOpen({ ...isOpen, seniority: false });
          }}
        />
        <Dropdown 
          label="Industry" 
          value={preferences.industry} 
          options={industries} 
          fieldKey="industry"
          isOpen={isOpen.industry}
          onToggle={() => setIsOpen({ ...isOpen, industry: !isOpen.industry })}
          onSelect={(value) => {
            handleFieldChange('industry', value);
            setIsOpen({ ...isOpen, industry: false });
          }}
        />
      </div>

      {/* Role Row */}
      <div className="mb-4 md:mb-6">
        <TextInput 
          label="Role" 
          value={preferences.role} 
          fieldKey="role"
          placeholder="e.g., Software Engineer, Product Manager"
          onChange={(value) => handleFieldChange('role', value)}
        />
      </div>

      {/* Update Button */}
      <div className="flex justify-end">
        <button
          onClick={handleUpdate}
          disabled={!hasChanges || isUpdating}
          className={`w-full md:w-auto px-4 md:px-6 py-2 md:py-2.5 rounded-lg font-medium text-xs md:text-sm transition-all ${
            hasChanges && !isUpdating
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isUpdating ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Updating...
            </span>
          ) : (
            'Update Preferences'
          )}
        </button>
      </div>
    </div>
  );
};

